import { useState, useEffect } from "react";
import AdminLayout from "../components/admin/AdminLayout";
import { useAdminDashboard } from "../services/admin/AdminDashboardService";
import { useNotification } from "../context/NotificationContext";
import AdminCategoryService from "../services/admin/AdminCategoryServices";
import AdminProductService from "../services/admin/AdminProductService";
import { useAddCategoryModal } from "../hooks/useAddCategoryModal";
import AddEditProductModal from "../components/admin/AddEditProductModal";
import AddCategoryModal from "../components/admin/AddCategoryModal/AddCategoryModal";
import {
  Package,
  FolderTree,
  Box,
  ShoppingBag,
  FileText,
  AlertCircle,
  TrendingUp,
  Clock,
  XCircle,
  Image,
  Video,
  ExternalLink,
  Plus,
  FileStack,
  User,
  ArrowRight,
  Loader2,
} from "lucide-react";

export default function AdminDashboard() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [hoveredActivity, setHoveredActivity] = useState(null);
  const [hoveredStatus, setHoveredStatus] = useState(null);

  // Modal Visibility States
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);

  // Data States for Modals
  const [categories, setCategories] = useState([]);
  const [unitMeasurements, setUnitMeasurements] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const { stats, loading, fetchDashboardStats } = useAdminDashboard();
  const { showNotification } = useNotification();

  // Category Modal State Hook
  const {
    newCategories,
    setNewCategories,
    handleImageUpload,
    addAnotherCategory,
    removeCategory,
    resetCategories,
    getValidCategories,
  } = useAddCategoryModal();

  useEffect(() => {
    fetchDashboardStats();
    loadDependencyData();
  }, [fetchDashboardStats]);

  const loadDependencyData = async () => {
    setIsDataLoading(true);
    try {
      const [categoryList, units] = await Promise.all([
        AdminCategoryService.list(),
        AdminCategoryService.getUnitMeasurements(),
      ]);
      setCategories(categoryList || []);
      setUnitMeasurements(units || []);
    } catch (err) {
      console.error("Failed to fetch dashboard dependency data:", err);
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleAddProductSave = async ({ mode, productId, formData }) => {
    showNotification({
      type: "info",
      title: "Saving Product",
      message: `${mode === "edit" ? "Updating" : "Creating"} product, please wait...`,
    });

    try {
      let res;
      if (mode === "edit") {
        res = await AdminProductService.update(productId, formData);
      } else {
        res = await AdminProductService.create(formData);
      }
      const { data: createdProducts, errors } = res.data;

      if (createdProducts && createdProducts.length > 0) {
        showNotification({
          type: "success",
          title: "Product Created",
          message: createdProducts.length > 1
            ? `Successfully created ${createdProducts.length} products.`
            : `"${createdProducts[0]?.name || "Product"}" has been created successfully.`,
        });
        // Refresh stats to reflect new product
        fetchDashboardStats();
      }

      if (errors && errors.length > 0) {
        errors.forEach((err) => {
          showNotification({
            type: "error",
            title: `Item ${err.index + 1} Error`,
            message: err.message,
          });
        });
      }

      setShowAddProductModal(false);
    } catch (err) {
      console.error("Product creation failed", err);
      const errorData = err?.response?.data;
      showNotification({
        type: "error",
        title: "Save Failed",
        message: errorData?.message || "Failed to save product",
      });
    }
  };

  const handleAddCategorySubmit = async () => {
    const validCategories = getValidCategories();
    if (validCategories.length === 0) {
      showNotification({
        type: "warning",
        title: "Incomplete Data",
        message: "Please fill in all required fields for at least one category.",
      });
      return;
    }

    showNotification({
      type: "info",
      title: "Saving Categories",
      message: "Adding new categories, please wait...",
    });

    try {
      const response = await AdminCategoryService.create(validCategories);
      const createdCount = Array.isArray(response?.data) ? response.data.length : 0;
      const errors = Array.isArray(response?.errors) ? response.errors : [];

      if (createdCount > 0) {
        showNotification({
          type: "success",
          title: "Categories Created",
          message: `Successfully added ${createdCount} categories.`,
        });
        resetCategories();
        setShowAddCategoryModal(false);
        // Refresh data & stats
        loadDependencyData();
        fetchDashboardStats();
      }

      if (errors.length > 0) {
        errors.forEach((err) => {
          showNotification({
            type: "error",
            title: `Error at Index ${err.index}`,
            message: err.message,
          });
        });
      }
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to add categories";
      showNotification({
        type: "error",
        title: "Submission Error",
        message: message,
      });
    }
  };

  // Helper for date formatting
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "Never") return "Never";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  // Empty State Component
  const EmptyState = ({ icon: Icon, title, message }) => (
    <div className="flex flex-col items-center justify-center py-10 px-4 text-center bg-[#FAFAFA] dark:bg-[#1A1A1A] rounded-xl border border-dashed border-[#E5E7EB] dark:border-[#2A2A2A] animate-fade-in">
      <div className="w-12 h-12 rounded-full bg-[#F5F3FF] dark:bg-[#2A2440] flex items-center justify-center mb-4">
        <Icon size={24} className="text-[#62397f] dark:text-[#A78BFA]" />
      </div>
      <h3 className="text-sm font-semibold text-[#111111] dark:text-white font-inter mb-1">
        {title}
      </h3>
      <p className="text-xs text-[#666666] dark:text-[#A0A0A0] font-inter max-w-[200px]">
        {message}
      </p>
    </div>
  );

  // KPI Data
  const kpiCards = [
    {
      id: "products",
      icon: Package,
      label: "Total Products",
      value: stats?.kpi?.total_products ?? "...",
      subtext: "All inventory items",
      color: "#62397f",
      path: "/admin/inventory/products",
    },
    {
      id: "categories",
      icon: FolderTree,
      label: "Active Categories",
      value: stats?.kpi?.active_categories ?? "...",
      subtext: "Organized groups",
      color: "#62397f",
      path: "/admin/inventory/categories",
    },
    {
      id: "available",
      icon: Box,
      label: "Available Items",
      value: stats?.kpi?.available_items ?? "...",
      subtext: "Ready to rent",
      color: "#22C55E",
      path: "/admin/inventory/products?filter=available",
    },
    {
      id: "rentals",
      icon: ShoppingBag,
      label: "Active Rentals",
      value: "0",
      subtext: "Coming soon",
      color: "#F59E0B",
      path: "/admin/invoices?filter=active",
    },
    {
      id: "invoices",
      icon: FileText,
      label: "Pending Invoices",
      value: "0",
      subtext: "Coming soon",
      color: "#EF4444",
      path: "/admin/invoices?filter=pending",
    },
    {
      id: "disabled",
      icon: XCircle,
      label: "Disabled Products",
      value: stats?.kpi?.disabled_products ?? "...",
      subtext: "Hidden from site",
      color: "#6B7280",
      path: "/admin/inventory/products?filter=disabled",
    },
  ];

  // Data initialized as empty for backend integration
  const attentionItems = [];
  const recentAlerts = [];
  const topCategories = [];
  const stockStatus = [];
  const recentActivity = [];
  const rentalStatuses = [];

  const handleCardClick = (path) => {
    if (typeof window !== "undefined" && path) {
      window.location.href = path;
    }
  };

  const handleQuickAction = (action) => {
    if (typeof window !== "undefined") {
      if (action === "product") {
        setShowAddProductModal(true);
      } else if (action === "category") {
        setShowAddCategoryModal(true);
      } else if (action === "catalog") {
        window.location.href = "/admin/catalog/";
      }
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#111111] dark:text-white font-poppins mb-2">
                Dashboard
              </h1>
              <p className="text-sm md:text-base text-[#666666] dark:text-[#A0A0A0] font-inter">
                Overview of rentals, inventory, and activity
              </p>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleQuickAction("product")}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white rounded-xl font-semibold text-sm font-inter transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95"
              >
                <Plus size={18} />
                <span className="hidden sm:inline">Add Product</span>
                <span className="sm:hidden">Product</span>
              </button>
              <button
                onClick={() => handleQuickAction("category")}
                className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-[#262626] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#62397f] dark:text-[#A78BFA] rounded-xl font-semibold text-sm font-inter transition-all duration-200 hover:bg-[#F9FAFB] dark:hover:bg-[#1E1E1E] hover:scale-105 active:scale-95"
              >
                <Plus size={18} />
                <span className="hidden sm:inline">Add Category</span>
                <span className="sm:hidden">Category</span>
              </button>
              <button
                onClick={() => handleQuickAction("catalog")}
                className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-[#262626] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111111] dark:text-white rounded-xl font-semibold text-sm font-inter transition-all duration-200 hover:bg-[#F9FAFB] dark:hover:bg-[#1E1E1E] hover:scale-105 active:scale-95"
              >
                <FileStack size={18} />
                <span className="hidden sm:inline">View Catalog</span>
                <span className="sm:hidden">Catalog</span>
              </button>
            </div>
          </div>
        </div>

        {/* KPI Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          {kpiCards.map((card) => {
            const Icon = card.icon;
            const isHovered = hoveredCard === card.id;

            return (
              <button
                key={card.id}
                onClick={() => handleCardClick(card.path)}
                onMouseEnter={() => setHoveredCard(card.id)}
                onMouseLeave={() => setHoveredCard(null)}
                className={`bg-white dark:bg-[#1E1E1E] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl p-5 transition-all duration-200 cursor-pointer text-left ${isHovered
                  ? "shadow-lg scale-105 border-[#62397f] dark:border-[#7C3AED]"
                  : "hover:shadow-md active:scale-95"
                  }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{
                      backgroundColor: `${card.color}15`,
                    }}
                  >
                    <Icon size={20} style={{ color: card.color }} />
                  </div>
                </div>
                <div className="text-3xl font-bold text-[#111111] dark:text-white font-poppins mb-1">
                  {card.value}
                </div>
                <div className="text-sm font-medium text-[#666666] dark:text-[#A0A0A0] font-inter mb-1">
                  {card.label}
                </div>
                <div className="text-xs text-[#999999] dark:text-[#777777] font-inter">
                  {card.subtext}
                </div>
              </button>
            );
          })}
        </div>

        {/* Action Required Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Attention Needed */}
          <div className="bg-white dark:bg-[#1E1E1E] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <AlertCircle size={20} className="text-[#EF4444]" />
              <h2 className="text-xl font-bold text-[#111111] dark:text-white font-poppins">
                Attention Needed
              </h2>
            </div>
            <div className="space-y-3">
              {attentionItems.length > 0 ? (
                attentionItems.map((item) => {
                  const isHovered = hoveredAction === item.id;
                  const badgeColors = {
                    red: "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400",
                    orange:
                      "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-600 dark:text-orange-400",
                    yellow:
                      "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-600 dark:text-yellow-400",
                  };

                  return (
                    <button
                      key={item.id}
                      onMouseEnter={() => setHoveredAction(item.id)}
                      onMouseLeave={() => setHoveredAction(null)}
                      className={`w-full p-4 rounded-lg border transition-all duration-200 text-left ${isHovered
                        ? "bg-[#F9FAFB] dark:bg-[#262626] border-[#62397f] dark:border-[#7C3AED] scale-[1.02]"
                        : "bg-[#FAFAFA] dark:bg-[#1A1A1A] border-[#E5E7EB] dark:border-[#2A2A2A] hover:bg-[#F9FAFB] dark:hover:bg-[#262626]"
                        }`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="font-semibold text-sm text-[#111111] dark:text-white font-inter flex-1">
                          {item.title}
                        </h3>
                        <span
                          className={`px-2.5 py-1 text-xs font-medium rounded-full border whitespace-nowrap ${badgeColors[item.badgeColor]}`}
                        >
                          {item.badge}
                        </span>
                      </div>
                      <p className="text-sm text-[#666666] dark:text-[#A0A0A0] font-inter mb-3">
                        {item.description}
                      </p>
                      <div className="flex items-center gap-1 text-[#62397f] dark:text-[#A78BFA] text-sm font-medium font-inter">
                        View Details
                        <ArrowRight size={14} />
                      </div>
                    </button>
                  );
                })
              ) : (
                <EmptyState
                  icon={AlertCircle}
                  title="All Caught Up"
                  message="There are no items requiring immediate attention at this time."
                />
              )}
            </div>
          </div>

          {/* Recent Alerts */}
          <div className="bg-white dark:bg-[#1E1E1E] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <Clock size={20} className="text-[#62397f]" />
              <h2 className="text-xl font-bold text-[#111111] dark:text-white font-poppins">
                Recent Alerts
              </h2>
            </div>
            <div className="space-y-3">
              {recentAlerts.length > 0 ? (
                recentAlerts.map((alert) => {
                  const Icon = alert.icon;
                  const isHovered = hoveredAction === `alert-${alert.id}`;

                  return (
                    <button
                      key={alert.id}
                      onMouseEnter={() => setHoveredAction(`alert-${alert.id}`)}
                      onMouseLeave={() => setHoveredAction(null)}
                      className={`w-full p-4 rounded-lg border transition-all duration-200 text-left ${isHovered
                        ? "bg-[#F9FAFB] dark:bg-[#262626] border-[#62397f] dark:border-[#7C3AED] scale-[1.02]"
                        : "bg-[#FAFAFA] dark:bg-[#1A1A1A] border-[#E5E7EB] dark:border-[#2A2A2A] hover:bg-[#F9FAFB] dark:hover:bg-[#262626]"
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#F5F3FF] dark:bg-[#2A2440] flex items-center justify-center flex-shrink-0">
                          <Icon
                            size={16}
                            className="text-[#62397f] dark:text-[#A78BFA]"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm text-[#111111] dark:text-white font-inter mb-1">
                            {alert.title}
                          </h3>
                          <p className="text-sm text-[#666666] dark:text-[#A0A0A0] font-inter mb-2">
                            {alert.description}
                          </p>
                          <span className="text-xs text-[#999999] dark:text-[#777777] font-inter">
                            {alert.time}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <EmptyState
                  icon={Clock}
                  title="No Recent Alerts"
                  message="System alerts and important notifications will appear here."
                />
              )}
            </div>
          </div>
        </div>

        {/* Inventory Health Overview */}
        <div className="bg-white dark:bg-[#1E1E1E] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl p-6 mb-8">
          <h2 className="text-xl font-bold text-[#111111] dark:text-white font-poppins mb-6">
            Inventory Overview
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Inventory Breakdown */}
            <div>
              <h3 className="text-sm font-semibold text-[#666666] dark:text-[#A0A0A0] font-inter uppercase tracking-wide mb-4">
                Top Categories
              </h3>
              <div className="space-y-3">
                {topCategories.length > 0 ? (
                  topCategories.map((category, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg bg-[#FAFAFA] dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-all duration-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#62397f]"></div>
                        <span className="font-medium text-sm text-[#111111] dark:text-white font-inter">
                          {category.name}
                        </span>
                      </div>
                      <span className="font-bold text-sm text-[#111111] dark:text-white font-poppins">
                        {category.count}
                      </span>
                    </div>
                  ))
                ) : (
                  <EmptyState
                    icon={FolderTree}
                    title="No Categories Yet"
                    message="Add categories to your inventory to see them listed here."
                  />
                )}
              </div>
              <button className="mt-4 text-sm font-medium text-[#62397f] dark:text-[#A78BFA] hover:underline font-inter flex items-center gap-1">
                View All Categories
                <ArrowRight size={14} />
              </button>
            </div>

            {/* Stock Status */}
            {/* <div>
              <h3 className="text-sm font-semibold text-[#666666] dark:text-[#A0A0A0] font-inter uppercase tracking-wide mb-4">
                Stock Status
              </h3>
              <div className="space-y-4">
                {stockStatus.map((status, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-[#111111] dark:text-white font-inter">
                        {status.label}
                      </span>
                      <span className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                        {status.count}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-[#E5E7EB] dark:bg-[#2A2A2A] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${(status.count / 1025) * 100}%`,
                          backgroundColor: status.color,
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div> */}
          </div>
        </div>

        {/* Recent Activity & Invoice Pipeline */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Recent Activity */}
          <div className="bg-white dark:bg-[#1E1E1E] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl p-6">
            <h2 className="text-xl font-bold text-[#111111] dark:text-white font-poppins mb-5">
              Recent Activity
            </h2>
            <div className="space-y-3">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity) => {
                  const isHovered = hoveredActivity === activity.id;

                  return (
                    <div
                      key={activity.id}
                      onMouseEnter={() => setHoveredActivity(activity.id)}
                      onMouseLeave={() => setHoveredActivity(null)}
                      className={`p-3 rounded-lg transition-all duration-200 ${isHovered
                        ? "bg-[#F9FAFB] dark:bg-[#262626]"
                        : "hover:bg-[#FAFAFA] dark:hover:bg-[#1A1A1A]"
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#62397f] to-[#7C3AED] flex items-center justify-center flex-shrink-0">
                          <User size={14} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#111111] dark:text-white font-inter">
                            <span className="font-semibold">
                              {activity.admin}
                            </span>{" "}
                            <span className="text-[#666666] dark:text-[#A0A0A0]">
                              {activity.action}
                            </span>{" "}
                            <span className="font-semibold">
                              {activity.entity}
                            </span>
                          </p>
                          <span className="text-xs text-[#999999] dark:text-[#777777] font-inter">
                            {activity.time}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <EmptyState
                  icon={FileText}
                  title="No Recent Activity"
                  message="Activity logs will be recorded as you and other admins manage the system."
                />
              )}
            </div>
            <button className="mt-4 text-sm font-medium text-[#62397f] dark:text-[#A78BFA] hover:underline font-inter flex items-center gap-1">
              View Full Audit Logs
              <ExternalLink size={14} />
            </button>
          </div>

          {/* Invoice & Rental Pipeline */}
          <div className="bg-white dark:bg-[#1E1E1E] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl p-6">
            <h2 className="text-xl font-bold text-[#111111] dark:text-white font-poppins mb-5">
              Rental & Invoice Status
            </h2>
            <div className="space-y-3">
              {rentalStatuses.length > 0 ? (
                rentalStatuses.map((status, index) => {
                  const isHovered = hoveredStatus === index;

                  return (
                    <button
                      key={index}
                      onMouseEnter={() => setHoveredStatus(index)}
                      onMouseLeave={() => setHoveredStatus(null)}
                      className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all duration-200 ${isHovered
                        ? "bg-[#F9FAFB] dark:bg-[#262626] border-[#62397f] dark:border-[#7C3AED] scale-105"
                        : "bg-[#FAFAFA] dark:bg-[#1A1A1A] border-[#E5E7EB] dark:border-[#2A2A2A] hover:bg-[#F9FAFB] dark:hover:bg-[#262626]"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: status.color }}
                        ></div>
                        <span className="font-medium text-sm text-[#111111] dark:text-white font-inter">
                          {status.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-[#111111] dark:text-white font-poppins">
                          {status.count}
                        </span>
                        <ArrowRight
                          size={16}
                          className="text-[#62397f] dark:text-[#A78BFA]"
                        />
                      </div>
                    </button>
                  );
                })
              ) : (
                <EmptyState
                  icon={ShoppingBag}
                  title="No Rental Data"
                  message="Active rentals and invoice statuses will be tracked here once created."
                />
              )}
            </div>
          </div>
        </div>

        {/* Catalog & Gallery Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Catalog Status */}
          <div className="bg-white dark:bg-[#1E1E1E] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileStack size={20} className="text-[#62397f]" />
              <h2 className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                Catalog Status
              </h2>
              {loading && <Loader2 size={16} className="animate-spin text-[#62397f]" />}
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#666666] dark:text-[#A0A0A0] font-inter">
                  Uploaded
                </span>
                <span className={`px-3 py-1 border text-xs font-medium rounded-full ${stats?.catalog?.is_available === "Yes"
                  ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400"
                  : "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400"
                  }`}>
                  {stats?.catalog?.is_available ?? "No"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#666666] dark:text-[#A0A0A0] font-inter">
                  Last Updated
                </span>
                <span className="text-sm font-medium text-[#111111] dark:text-white font-inter">
                  {formatDate(stats?.catalog?.last_updated)}
                </span>
              </div>
              <button
                onClick={() => handleQuickAction("catalog")}
                className="w-full mt-4 py-2.5 px-4 bg-[#F9FAFB] dark:bg-[#262626] border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#62397f] dark:text-[#A78BFA] rounded-lg font-medium text-sm font-inter transition-all duration-200 hover:bg-[#F5F3FF] dark:hover:bg-[#2A2440] hover:border-[#62397f] dark:hover:border-[#7C3AED] active:scale-95 flex items-center justify-center gap-2"
              >
                <ExternalLink size={16} />
                View / Replace Catalog
              </button>
            </div>
          </div>

          {/* Gallery Summary */}
          <div className="bg-white dark:bg-[#1E1E1E] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Image size={20} className="text-[#62397f]" />
              <h2 className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                Gallery Summary
              </h2>
              {loading && <Loader2 size={16} className="animate-spin text-[#62397f]" />}
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Image
                    size={16}
                    className="text-[#666666] dark:text-[#A0A0A0]"
                  />
                  <span className="text-sm text-[#666666] dark:text-[#A0A0A0] font-inter">
                    Total Images
                  </span>
                </div>
                <span className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                  {stats?.gallery?.total_images ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Video
                    size={16}
                    className="text-[#666666] dark:text-[#A0A0A0]"
                  />
                  <span className="text-sm text-[#666666] dark:text-[#A0A0A0] font-inter">
                    Total Videos
                  </span>
                </div>
                <span className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                  {stats?.gallery?.total_videos ?? 0}
                </span>
              </div>
              <div className="pt-2 border-t border-[#E5E7EB] dark:border-[#2A2A2A]">
                <span className="text-xs text-[#999999] dark:text-[#777777] font-inter">
                  {(stats?.gallery?.total_images > 0 || stats?.gallery?.total_videos > 0)
                    ? "Content synchronization active."
                    : "No recent uploads detected."}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Add Product Modal */}
      {showAddProductModal && (
        <AddEditProductModal
          isOpen={showAddProductModal}
          isEditMode={false}
          product={null}
          categories={categories}
          onClose={() => setShowAddProductModal(false)}
          onSave={handleAddProductSave}
          onAddCategory={(newCategory) =>
            setCategories((prev) => [...prev, newCategory])
          }
        />
      )}

      {/* Quick Add Category Modal */}
      {showAddCategoryModal && (
        <AddCategoryModal
          newCategories={newCategories}
          setNewCategories={setNewCategories}
          handleImageUpload={handleImageUpload}
          addAnotherCategory={addAnotherCategory}
          removeCategory={removeCategory}
          handleAddCategory={handleAddCategorySubmit}
          setShowAddModal={setShowAddCategoryModal}
          unitMeasurements={unitMeasurements}
        />
      )}
    </AdminLayout>
  );
}


